/**
 * NudgeCTA — a short, centered prompt directing visitors to call to
 * schedule a tour. Reusable across pages via the `tokenPrefix` prop;
 * each `pages.json` instance with a unique prefix gets its own
 * independently editable message and phone CTA.
 *
 * The headline is editable richtext; the phone button defaults to
 * `property_call_tracking_phone` and renders a `tel:` link so taps from
 * mobile devices place a call.
 */

import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  usePropertyField,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { formatDisplayPhone } from '@vibesites/theme-preferred/components/PropertyContactInfo';

const phoneLinkDef: SlotGroupDefinition = {
  label: 'Phone CTA',
  fields: {
    phone: {
      label: 'Phone Number',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      suggestedFields: ['property_call_tracking_phone', 'property_phone'],
    },
    label: {
      label: 'Button Text',
      type: 'text',
      defaultValue: 'Call Now',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

interface NudgeCTAProps {
  tokenPrefix?: string;
}

export function NudgeCTA({ tokenPrefix }: NudgeCTAProps = {}) {
  const prefix = tokenPrefix || 'nudgecta';
  const messagePath = `${prefix}.message`;
  const phoneLinkPath = `${prefix}.phone_link`;

  const cta = useSlotGroup(phoneLinkPath, phoneLinkDef);
  const fallbackPhone = usePropertyField('property_call_tracking_phone') as string | null;
  const phone = cta.phone || fallbackPhone || '';
  const telHref = `tel:${phone.replace(/[^+\d]/g, '')}`;
  const displayPhone = formatDisplayPhone(phone);
  const buttonLabel = cta.label || 'Call Now';

  return (
    <section className="nudge-cta-section">
      <div className="nudge-cta-container">
        <RichTextContent
          path={messagePath}
          label="Message"
          defaultValue="<p>Please call us to schedule an appointment to tour</p>"
          as="p"
          className="nudge-cta-message"
        />

        <EditableSlotGroup groupPath={phoneLinkPath} definition={phoneLinkDef}>
          <a
            href={phone ? telHref : '#'}
            target={cta._target || '_self'}
            className="dniphone dniphonehref nudge-cta-button"
            type="call"
          >
            <svg
              className="nudge-cta-icon"
              width="18"
              height="18"
              viewBox="0 0 20 20"
              fill="none"
              aria-hidden="true"
            >
              <path
                d="M4 2a2 2 0 00-2 2v1a13 13 0 0013 13h1a2 2 0 002-2v-2.5a1 1 0 00-.68-.95l-3.12-1a1 1 0 00-1.02.27l-1.38 1.38A11 11 0 015.8 7.2l1.38-1.38a1 1 0 00.27-1.02l-1-3.12A1 1 0 005.5 1H4z"
                fill="currentColor"
              />
            </svg>
            <span>
              {displayPhone ? `${buttonLabel}: ${displayPhone}` : buttonLabel}
            </span>
          </a>
        </EditableSlotGroup>

        {/* Schema-lint anchors — never visible, never interactive.
            They carry the canonical schema-key literals so the source-level
            linter can match them against contentSchema. The visible wrappers
            above use the dynamic per-instance path. */}
        <RichTextContent
          path="nudgecta.message"
          label="Message"
          defaultValue="<p>Please call us to schedule an appointment to tour</p>"
          as="span"
          className="hidden"
          style={{ display: 'none' }}
        />
        <EditableSlotGroup
          groupPath="nudgecta.phone_link"
          definition={phoneLinkDef}
          className="hidden"
          style={{ display: 'none' }}
        >
          <span />
        </EditableSlotGroup>
      </div>

      <style>{`
        .nudge-cta-section {
          padding: 2rem 1rem;
          background: color-mix(in srgb, var(--vibe-color-primary) 8%, white);
          border-top: 1px solid color-mix(in srgb, var(--vibe-color-primary) 15%, transparent);
          border-bottom: 1px solid color-mix(in srgb, var(--vibe-color-primary) 15%, transparent);
        }
        @media (min-width: 768px) {
          .nudge-cta-section { padding: 2.5rem 1.5rem; }
        }
        .nudge-cta-container {
          max-width: 60rem;
          margin: 0 auto;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1rem;
          text-align: center;
        }
        @media (min-width: 768px) {
          .nudge-cta-container {
            flex-direction: row;
            justify-content: center;
            gap: 1.75rem;
            text-align: left;
          }
        }
        .nudge-cta-message {
          font-family: var(--vibe-font-heading);
          font-size: 1.125rem;
          line-height: 1.4;
          font-weight: 600;
          color: var(--vibe-color-text);
          margin: 0;
          letter-spacing: 0.01em;
        }
        @media (min-width: 768px) {
          .nudge-cta-message { font-size: 1.25rem; }
        }
        .nudge-cta-message :where(p) { margin: 0; }
        .nudge-cta-button {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          padding: 0.75rem 1.5rem;
          font-family: var(--vibe-font-heading);
          font-size: 0.875rem;
          font-weight: 600;
          letter-spacing: 0.05em;
          text-transform: uppercase;
          text-decoration: none;
          color: var(--vibe-color-text-on-primary, #fff);
          background: var(--vibe-color-primary);
          border-radius: 9999px;
          white-space: nowrap;
          transition: transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease;
        }
        .nudge-cta-button:hover {
          transform: translateY(-1px);
          box-shadow: 0 6px 16px rgba(0, 0, 0, 0.15);
          background: color-mix(in srgb, var(--vibe-color-primary) 88%, black);
        }
        .nudge-cta-icon {
          flex-shrink: 0;
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'NudgeCTA',
  displayName: 'Nudge CTA',
  tokens: {
    'nudgecta.message': {
      label: 'Message',
      type: 'richtext',
      defaultValue: '<p>Please call us to schedule an appointment to tour</p>',
    },
  },
  slotGroups: {
    'nudgecta.phone_link': phoneLinkDef,
  },
};

(NudgeCTA as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default NudgeCTA;
